import React, { useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  AccessTime,
  LocalOffer,
  Check,
  Warning,
  LocalShipping,
  ShoppingCart,
  BusinessCenter,
  Info,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import Loading from '../../components/global/loading/Loading';
import { useOffer } from '../../context/OfferContext';
import { Offer as OfferModel } from '../../models/Offer';
import translateDeliveryMethod from '../../helperFunctions/translateDeliveryMethod';
import getIconForDeliveryMethod from '../../helperFunctions/getIconForDeliveryMethod';
import { useConfig } from '../../utils/useConfig';
import OfferCart from './OfferCart';
import Seo from '../../components/seo/Seo';

function Offer() {
  const { id } = useParams();
  const config = useConfig();
  const { getOffer } = useOffer();
  const [offer, setOffer] = useState<OfferModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchOffer = async () => {
      setIsLoading(true);
      try {
        const offer = await getOffer(id);
        setOffer(offer);
      } catch (error) {
        console.error('Failed to fetch offer', error);
        toast.error(
          'Nie udało się załadować oferty. Spróbuj ponownie później.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchOffer();
  }, [getOffer, id]);

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  // Format daty
  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Sprawdzenie czy oferta jest ważna
  const isValid = offer ? new Date(offer.validUntil) > new Date() : false;

  // Sprawdzenie czy można złożyć zamówienie (oferta jest ważna i nie jest zaakceptowana)
  const canOrder = isValid && offer ? !offer.isAccepted : false;

  // Obliczenie pozostałych dni
  const getRemainingDays = () => {
    if (!offer) return 0;

    const today = new Date();
    const validUntil = new Date(offer.validUntil);
    const diffTime = validUntil.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (!offer) {
    return (
      <>
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">
            <h2 className="text-xl font-medium mb-2">Nie znaleziono oferty</h2>
            <p className="mb-4">
              Oferta o podanym identyfikatorze nie istnieje lub została
              usunięta.
            </p>
            <Link to="/" className="text-red-700 font-medium hover:underline">
              Powrót do strony głównej
            </Link>
          </div>
        </div>
        <Seo
          title={`Oferta nie została znaleziona`}
          description="Nie znaleziono oferty o podanym identyfikatorze."
          image={config.company.logo}
          noindex={true}
        />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <motion.div
              className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Nagłówek oferty */}
              <motion.div className="mb-8" variants={itemVariants}>
                <h1 className="text-2xl font-medium text-gray-900 mb-2 flex items-center">
                  <LocalOffer className="text-primary mr-2" />
                  Oferta:{' '}
                  <span className="font-bold text-primary ml-2">
                    {offer.offerNumber}
                  </span>
                </h1>

                <p className="text-gray-600 mb-4">
                  Data utworzenia: {formatDate(offer.sentAt)}
                </p>

                <div className="flex flex-wrap gap-3 mt-4">
                  <div
                    className={`px-3 py-1.5 rounded-full flex items-center gap-1 text-sm font-medium ${
                      isValid
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    {isValid ? (
                      <Check fontSize="small" />
                    ) : (
                      <Warning fontSize="small" />
                    )}
                    {isValid ? 'Oferta ważna' : 'Oferta wygasła'}
                  </div>

                  {isValid && (
                    <div className="px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium flex items-center gap-1">
                      <AccessTime fontSize="small" />
                      Ważna do: {formatDate(offer.validUntil)}
                    </div>
                  )}

                  {offer.isAccepted && (
                    <div className="px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-sm font-medium flex items-center gap-1">
                      <Check fontSize="small" />
                      Oferta zaakceptowana
                    </div>
                  )}
                </div>

                {isValid && (
                  <div className="mt-3 text-gray-600">
                    Pozostało:{' '}
                    <span className="font-medium">
                      {getRemainingDays()} dni
                    </span>
                  </div>
                )}
              </motion.div>

              {/* Alert o wygaśnięciu oferty */}
              {!isValid && (
                <motion.div
                  className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3"
                  variants={itemVariants}
                >
                  <div className="p-2 bg-red-100 rounded-full text-red-600">
                    <Warning />
                  </div>
                  <div>
                    <h3 className="font-medium text-red-800 mb-1">
                      Ta oferta wygasła
                    </h3>
                    <p className="text-red-700">
                      Oferta straciła ważność {formatDate(offer.validUntil)}.
                      Skontaktuj się z nami, aby odświeżyć ofertę.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Alert o zaakceptowanej ofercie */}
              {isValid && offer.isAccepted && (
                <motion.div
                  className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 flex items-center gap-3"
                  variants={itemVariants}
                >
                  <div className="p-2 bg-purple-100 rounded-full text-purple-600">
                    <Check />
                  </div>
                  <div>
                    <h3 className="font-medium text-purple-800 mb-1">
                      Ta oferta została już zaakceptowana
                    </h3>
                    <p className="text-purple-700">
                      Zamówienie na podstawie tej oferty zostało już złożone.
                      Jeśli chcesz złożyć dodatkowe zamówienie, skontaktuj się z
                      nami.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Alert o możliwości złożenia zamówienia */}
              {canOrder && (
                <motion.div
                  className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3"
                  variants={itemVariants}
                >
                  <div className="p-2 bg-blue-100 rounded-full text-blue-600">
                    <Info />
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-medium text-blue-800 mb-1">
                      Oferta jest aktualna
                    </h3>
                    <p className="text-blue-700">
                      Możesz złożyć zamówienie bezpośrednio na podstawie tej
                      oferty klikając przycisk obok.
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate('zamów/logowanie')}
                    className="px-4 py-2 rounded-md font-medium flex items-center gap-2 whitespace-nowrap bg-primary text-white hover:bg-primary-dark transition-colors"
                  >
                    <ShoppingCart fontSize="small" />
                    Złóż zamówienie
                  </motion.button>
                </motion.div>
              )}

              {/* Główna sekcja oferty */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Lewa kolumna - informacje o kliencie i dostawie */}
                <motion.div variants={itemVariants}>
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    {/* Dane klienta */}
                    <div className="p-5 border-b border-gray-100">
                      <h3 className="text-base font-medium text-gray-800 mb-3 flex items-center">
                        <BusinessCenter
                          className="mr-2 text-primary"
                          fontSize="small"
                        />
                        Dane klienta
                      </h3>
                      <div className="space-y-2 text-gray-600">
                        <p>
                          <span className="text-gray-500">
                            Nazwa/Imię i nazwisko:
                          </span>{' '}
                          <span className="font-medium">
                            {offer.inquiry?.name}
                          </span>
                        </p>
                        {offer.inquiry?.email && (
                          <p>
                            <span className="text-gray-500">Email:</span>{' '}
                            <span className="font-medium">
                              {offer.inquiry.email}
                            </span>
                          </p>
                        )}
                        {offer.inquiry?.nip && (
                          <p>
                            <span className="text-gray-500">NIP:</span>{' '}
                            <span className="font-medium">
                              {offer.inquiry.nip}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Sposób dostawy */}
                    <div className="p-5 border-b border-gray-100">
                      <h3 className="text-base font-medium text-gray-800 mb-3 flex items-center">
                        <LocalShipping
                          className="mr-2 text-primary"
                          fontSize="small"
                        />
                        Sposób dostawy
                      </h3>
                      <div className="flex items-center">
                        <div className="p-2 bg-gray-50 rounded-md mr-3">
                          <img
                            src={getIconForDeliveryMethod(offer.deliveryMethod)}
                            alt={offer.deliveryMethod}
                            className="h-8 w-auto"
                          />
                        </div>
                        <div>
                          <div className="font-medium">
                            {translateDeliveryMethod(offer.deliveryMethod)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dodatkowe informacje */}
                    {offer.offerDetails && (
                      <div className="p-5">
                        <h3 className="text-base font-medium text-gray-800 mb-3 flex items-center">
                          <Info
                            className="mr-2 text-primary"
                            fontSize="small"
                          />
                          Dodatkowe informacje
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-md italic text-gray-700">
                          {offer.offerDetails}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sekcja kontaktowa */}
                  <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                      <h2 className="text-lg font-medium text-gray-800">
                        Kontakt w sprawie oferty
                      </h2>
                    </div>
                    <div className="p-5">
                      <p className="text-gray-600 mb-4">
                        Masz pytania dotyczące oferty? Skontaktuj się z nami:
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href="tel:+48604856094"
                          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Phone className="text-primary" />
                          <span className="font-medium">+48 604 856 094</span>
                        </a>
                        <a
                          href={`mailto:${config.contact.email}`}
                          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Mail className="text-primary" />
                          <span className="font-medium">
                            {config.contact.email}
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Prawa kolumna - podsumowanie oferty */}
                <motion.div variants={itemVariants}>
                  <div className="sticky top-8">
                    <h3 className="text-base font-medium text-gray-800 mb-3">
                      Produkty w ofercie
                    </h3>

                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-4">
                      <table className="w-full">
                        <thead className="bg-gray-50 text-left">
                          <tr>
                            <th className="px-4 py-3 text-sm font-medium text-gray-500">
                              Produkt
                            </th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-500 w-20 text-center">
                              Ilość
                            </th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-500 w-28 text-right">
                              Cena
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {offer.offerProducts.map((product) => (
                            <tr
                              key={product.productId}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0 h-10 w-10 rounded border border-gray-200 overflow-hidden">
                                    {product.product.mobilePrimaryImage ? (
                                      <img
                                        src={product.product.mobilePrimaryImage}
                                        alt={product.product.name}
                                        className="h-full w-full object-contain"
                                      />
                                    ) : (
                                      <div className="h-full w-full bg-gray-100 flex items-center justify-center">
                                        <span className="text-xs text-gray-500">
                                          ?
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <Link
                                      to={`/produkt/${product.product.slug}`}
                                      className="text-sm font-medium text-gray-900 hover:text-primary transition-colors"
                                    >
                                      {product.product.name}
                                    </Link>
                                    <p className="text-xs text-gray-500 truncate">
                                      SKU: {product.product.serialNumber}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-center">
                                <span className="font-medium">
                                  {product.quantity}
                                </span>
                                <span className="text-gray-500 text-xs ml-1">
                                  szt.
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-right">
                                <div className="flex flex-col items-end">
                                  {product.product.price &&
                                    product.product.price >
                                      product.pricePerProduct && (
                                      <>
                                        <span className="text-gray-500 line-through text-xs">
                                          {product.product.price.toFixed(2)} zł
                                        </span>
                                        <span className="text-green-600 text-xs">
                                          {Math.round(
                                            (1 -
                                              product.pricePerProduct /
                                                product.product.price) *
                                              100,
                                          )}
                                          % taniej
                                        </span>
                                      </>
                                    )}
                                  <span>
                                    {product.pricePerProduct.toFixed(2)} zł
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Podsumowanie */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">
                            Wartość produktów:
                          </span>
                          <div className="text-right">
                            {offer.offerProducts.some(
                              (p) =>
                                p.product.price &&
                                p.product.price > p.pricePerProduct,
                            ) && (
                              <>
                                <div className="text-gray-500 line-through text-sm">
                                  {offer.offerProducts
                                    .reduce(
                                      (acc, p) =>
                                        acc +
                                        (p.product.price || p.pricePerProduct) *
                                          p.quantity,
                                      0,
                                    )
                                    .toFixed(2)}{' '}
                                  zł
                                </div>
                                <div className="text-green-600 text-sm">
                                  Oszczędzasz:{' '}
                                  {offer.offerProducts
                                    .reduce(
                                      (acc, p) =>
                                        acc +
                                        ((p.product.price ||
                                          p.pricePerProduct) -
                                          p.pricePerProduct) *
                                          p.quantity,
                                      0,
                                    )
                                    .toFixed(2)}{' '}
                                  zł
                                </div>
                              </>
                            )}
                            <span className="font-medium">
                              {(offer.price - offer.deliveryPrice).toFixed(2)}{' '}
                              zł
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-gray-600">Dostawa:</span>
                          <span className="font-medium">
                            {offer.deliveryPrice.toFixed(2)} zł
                          </span>
                        </div>
                        <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                          <span className="font-medium text-gray-700">
                            Razem:
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {offer.price.toFixed(2)} zł
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Przycisk złożenia zamówienia */}
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      disabled={!canOrder}
                      onClick={() => {
                        if (canOrder) {
                          navigate('zamów/logowanie');
                        }
                      }}
                      className={`w-full mt-4 px-5 py-3 rounded-md font-medium flex items-center justify-center gap-2 ${
                        canOrder
                          ? 'bg-primary text-white hover:bg-primary-dark'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      } transition-colors`}
                    >
                      <>
                        <ShoppingCart />
                        {!isValid
                          ? 'Oferta wygasła'
                          : offer.isAccepted
                            ? 'Oferta już zaakceptowana'
                            : 'Złóż zamówienie na podstawie oferty'}
                      </>
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          }
        />
        <Route path="/zamów/*" element={<OfferCart offer={offer} />} />
      </Routes>
      <Seo
        title={`Oferta ${offer.offerNumber}`}
        description="Szczegóły oferty, dane kontaktowe oraz możliwość złożenia zamówienia."
        image={config.company.logo}
        noindex={false}
      />
    </>
  );
}

export default Offer;
